import React, { useEffect, useState } from "react";
import FloatingMenuList from "@components/FloatingMenuListMobile";
import { Checkbox, Grid, Typography, useTheme } from "@material-ui/core";
import useStyles from "./styles";
import { useMediaQuery } from "@mui/material";
import { ModalGeneral } from "@components/ModalGeneral/ModalGeneral";
import { enqueueSnackbar } from "notistack";

interface Product {
  id: string;
  name: string;
  volume: number;
  compartments: number[];
}

interface ProductCompartmentsModalProps {
  product: Product;
  availableCompartments: {
    id: number;
    label: string;
    volume: number;
  }[];
  onClose: () => void;
  onUpdateCompartments: (newCompartments: number[]) => void;
}

const ProductCompartmentsModal: React.FC<ProductCompartmentsModalProps> = ({
  product,
  availableCompartments,
  onClose,
  onUpdateCompartments,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const [isVolumeAllocated, setIsVolumeAllocated] = useState(false);
  const snackbarError = (message: string) => {
    enqueueSnackbar(message, {
      variant: "error",
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      style: { fontSize: "1.4rem" },
    });
  };

  const calculateTotalVolume = (idsToCheck, compartments) => {
    const filteredCompartments = compartments.filter((compartment) =>
      idsToCheck.includes(compartment.id)
    );

    const totalVolume = filteredCompartments.reduce(
      (sum, compartment) => sum + compartment.volume,
      0
    );

    return totalVolume;
  };

  const checksErrors = (
    id: number,
    selectedCompartmentsLocal: number[]
  ): string | null => {
    const volumeOV = product.volume;
    selectedCompartmentsLocal.push(id);

    const sumSelectedCompartmentsLocal = calculateTotalVolume(
      selectedCompartmentsLocal,
      availableCompartments
    );

    const isGreaterThanVolume = sumSelectedCompartmentsLocal > volumeOV;
    const isEqualToVolume = sumSelectedCompartmentsLocal === volumeOV;

    if (isGreaterThanVolume) {
      selectedCompartmentsLocal = selectedCompartmentsLocal.filter(
        (compartmentId) => compartmentId !== id
      );

      return `A seleção deste compartimento resultará em um volume total superior a ${volumeOV.toLocaleString()} L. Por favor, escolha um compartimento que se adeque a essa capacidade. `;
    }
    setIsVolumeAllocated(isEqualToVolume);
    return null;
  };

  const removeSelectedCompartments = (id: number) => {
    const newCompartments = product.compartments.filter(
      (compId) => compId !== id
    );
    onUpdateCompartments(newCompartments);
  };

  const addSelectedCompartments = (id: number) => {
    const compartmentToAdd = availableCompartments.find(
      (compartment) => compartment.id === id
    );
    if (!compartmentToAdd) return;

    const newCompartments = [...product.compartments, id];
    onUpdateCompartments(newCompartments);
  };

  const handleToggleCompartment = (id: number) => {
    const isSelected = product.compartments.includes(id);
    let selectedCompartmentsLocal = [...product.compartments];

    if (isSelected) {
      removeSelectedCompartments(id);
      return;
    }

    const errorMessage = checksErrors(id, selectedCompartmentsLocal);

    if (errorMessage) {
      snackbarError(errorMessage);
    } else {
      addSelectedCompartments(id);
    }
  };

  interface RenderCompartimentsProps {
    title?: string;
  }

  const RenderCompartiments: React.FC<RenderCompartimentsProps> = ({
    title,
  }) => {
    return (
      <>
        <Grid className={classes.centeredGrid}>
          {title && (
            <Typography variant="body1" className={classes.boldText}>
              {title}
            </Typography>
          )}

          <Typography
            className={classes.productInfoText}
            style={{
              paddingTop: isMobile ? "1rem" : "2rem",
              paddingBottom: isMobile ? "1rem" : "2rem",
            }}
          >
            {`${product.name} - ${product.volume.toLocaleString()} litros`}
          </Typography>
        </Grid>

        <div
          className={classes.container}
          style={{
            paddingRight: isMobile ? "1rem" : "2rem",
            paddingLeft: isMobile ? "1rem" : "2rem",
          }}
        >
          {availableCompartments.map((compartment, idx) => (
            <div key={compartment.id} className={classes.compartmentItem}>
              <label className={classes.label}>
                <Checkbox
                  checked={product.compartments.includes(compartment.id)}
                  onChange={() => handleToggleCompartment(compartment.id)}
                  className={classes.checkbox}
                  style={{ color: theme.palette.primary.main }}
                />
                {compartment.label}
              </label>
              <span className={classes.volumeText}>
                {compartment.volume.toLocaleString()} L
              </span>
            </div>
          ))}
        </div>
      </>
    );
  };

  useEffect(() => {
    if (isVolumeAllocated) {
      onClose();
    }
  }, [isVolumeAllocated, onClose]);

  return (
    <>
      {isMobile ? (
        <FloatingMenuList handleClose={onClose}>
          <RenderCompartiments title="Selecione o compartimento onde o produto será alocado" />
        </FloatingMenuList>
      ) : (
        <ModalGeneral
          open={!!product}
          closeModal={onClose}
          titleHeader="Selecione o compartimento onde o produto será alocado"
          width={"500px"}
          height={"400px"}
          hasDialogActions={false}
          resetPadding={true}
        >
          <RenderCompartiments />
        </ModalGeneral>
      )}
    </>
  );
};

export default ProductCompartmentsModal;
